import {
  fetchLatestReportState,
  fetchReportStateByWidgetId,
} from '@/store/thunks/FetchReport';
import { openWidget, OpenWidgetPayload } from '@/store/reducers/UIState';
import { ReportWidgetData, WidgetType } from '@/models/PanelWidgets';
import { ThreadId } from '@/models/Thread';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ReportWidgetStoreState {
  stateForThreads: Record<ThreadId, ReportWidgetData>;
}

const initialState: ReportWidgetStoreState = {
  stateForThreads: {},
};

/*************
 * reducers *
 *************/
export const cacheDataForThreadReducer = (
  state: ReportWidgetStoreState,
  action: PayloadAction<CacheReportPayload>,
) => {
  const { threadId, reportData } = action.payload;
  state.stateForThreads[threadId] = reportData;
};

export const openReportWidgetReducer = (
  state: ReportWidgetStoreState,
  action: PayloadAction<OpenWidgetPayload>,
) => {
  const { threadId, widgetData } = action.payload;
  if (!widgetData || widgetData.type !== WidgetType.REPORT) {
    return;
  }

  cacheDataForThreadReducer(state, {
    type: 'reportWidgetStore/cacheDataForThread',
    payload: {
      threadId,
      reportData: widgetData,
    },
  });
};

/*************
 * selectors *
 *************/
export const getReportStateForThreadSelector = (
  state: ReportWidgetStoreState,
  threadId: ThreadId | undefined,
): ReportWidgetData | null => {
  if (!threadId) {
    return null;
  }
  return state.stateForThreads[threadId];
};

/*************
 * slice *
 *************/
export const reportWidgetStoreSlice = createSlice({
  name: 'reportWidgetStore',
  initialState,
  reducers: {
    cacheReportForThread: cacheDataForThreadReducer,
  },
  extraReducers: (builder) => {
    builder
      .addCase(openWidget, openReportWidgetReducer)
      .addCase(fetchLatestReportState.fulfilled, cacheDataForThreadReducer)
      .addCase(fetchReportStateByWidgetId.fulfilled, cacheDataForThreadReducer);
  },
  selectors: {
    getReportStateForThread: getReportStateForThreadSelector,
  },
});

export const { getReportStateForThread } = reportWidgetStoreSlice.selectors;
export const { cacheReportForThread } = reportWidgetStoreSlice.actions;
export const reportWidgetStoreReducer = reportWidgetStoreSlice.reducer;

export interface CacheReportPayload {
  threadId: ThreadId;
  reportData: ReportWidgetData;
}
